

import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useAuth as useAuthContext } from '@/components/auth/AuthProvider'
import {
  SidebarProvider} from '@/components/ui/sidebar'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'
import { syncAuthState } from '@/utils/auth/auth-sync'
import { AppSidebar } from './AppSidebar'
import { UserPreferencesInitializer } from '@/components/preferences/UserPreferencesInitializer'
import { OpenInDesktopPrompt } from '@/components/shared/OpenInDesktopPrompt'
import { ProfileImagePreloader } from '@/components/shared/ProfileImagePreloader'
import {
  UPDATE_BANNER_HEIGHT,
  TauriUpdateBannerSlot,
  useUpdateBannerVisible,
} from '@/components/shared/UpdateBanner'
import { useOrganizationQueryInvalidation } from '@/hooks/useOrganizationQueryInvalidation'
import { isTauri, isPopupWindow, initializePopupWindowStatus } from '@/lib/utils/tauri'
import {
  TauriTitleBar,
  PopupNavBar,
  POPUP_NAV_BAR_HEIGHT,
  TAURI_TITLE_BAR_HEIGHT,
} from './TauriTitleBar'
import { useIsMobile } from '@/hooks/use-mobile'
import { BennyChat } from '@/components/assistant/BennyChat'
import { BennyCompanionController } from '@/components/desktop/BennyCompanionController'
import { AssistantClientActionsController } from '@/components/desktop/AssistantClientActionsController'
import { RecorderController } from '@/components/desktop/RecorderController'
import { MeetingReminderController } from '@/components/desktop/MeetingReminderController'
import { useRecordingStore } from '@/store/recording.store'
import { cn } from '@/lib/utils';

const LiveRecordingPanel = lazy(() =>
  import('@/components/meetings/LiveRecordingPanel').then(module => ({
    default: module.LiveRecordingPanel,
  })),
)

function LazyLiveRecordingPanel() {
  const state = useRecordingStore(recording => recording.state)
  const isOpen = useRecordingStore(recording => recording.isLivePanelOpen)
  const isActive =
    state === 'recording' || state === 'paused' || state === 'finalizing'

  if (!isActive || !isOpen) return null

  return (
    <Suspense fallback={null}>
      <LiveRecordingPanel />
    </Suspense>
  )
}

interface MainLayoutProps {
  children: ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const auth = useAuthContext();
  const sidebarOpen = useUIPreferencesStore(state => state.sidebarOpen);
  const isMobile = useIsMobile();

  // Check if this is a popup window (chromeless mode - no sidebar, no app title bar)
  // Use state to handle async initialization for Tauri window label check
  const [isPopup, setIsPopup] = useState(isPopupWindow());
  const hasInitializedPopup = useRef(false);

  useEffect(() => {
    if (hasInitializedPopup.current) return;
    hasInitializedPopup.current = true;

    // For Tauri, do async check of window label
    if (isTauri()) {
      initializePopupWindowStatus().then(result => {
        if (result !== isPopup) {
          setIsPopup(result);
        }
      });
    }
  }, [isPopup]);
  
  // Determine if we should show the Tauri title bar (not in popup mode)
  const isDesktopApp = isTauri() && !isMobile && !isPopup;
  const showUpdateBanner = useUpdateBannerVisible() && isDesktopApp;
  const desktopChromeHeight =
    isDesktopApp
      ? TAURI_TITLE_BAR_HEIGHT + (showUpdateBanner ? UPDATE_BANNER_HEIGHT : 0)
      : 0;
  const desktopShellVars = isDesktopApp
    ? ({
        '--tauri-shell-offset': `${desktopChromeHeight}px`,
        '--tauri-content-height': `calc(100vh - ${desktopChromeHeight}px)`,
      } as CSSProperties)
    : undefined;
  
  // Initialize organization query invalidation (invalidates queries on org change)
  // Note: Organizations are fetched by OrgRouteGuard/TodayRedirect - no need to pre-fetch here
  useOrganizationQueryInvalidation();
  
  // Simplified auth sync - remove setTimeout
  useEffect(() => {
    // Direct sync without timeout
    syncAuthState(auth.user, auth.isAuthenticated, auth.isLoading);
  }, [auth.isAuthenticated, auth.isLoading, auth.user]);
  
  // Organization setting is handled by OrgRouteGuard for org routes
  // and by TodayRedirect for the root route - no need to duplicate here
  
  // Popup mode: render without sidebar, but with minimal nav bar for Tauri
  // Still need TooltipProvider for components that use tooltips
  if (isPopup) {
    const showNavBar = isTauri() && !isMobile;
    return (
      <TooltipProvider delayDuration={0}>
        {showNavBar && <PopupNavBar />}
        <div
          className="min-h-svh h-svh w-full overflow-auto bg-background dark:bg-[hsl(var(--cast-iron-skillet))]"
          style={showNavBar ? { paddingTop: POPUP_NAV_BAR_HEIGHT } : undefined}
        >
          {auth.isAuthenticated && <UserPreferencesInitializer />}
          {auth.isAuthenticated ? children : null}
        </div>
      </TooltipProvider>
    );
  }
  
  return (
    <SidebarProvider 
      defaultOpen={sidebarOpen}
      style={{
        '--sidebar-width': '14rem',
        ...desktopShellVars,
      } as CSSProperties}
    >
      {/* Tauri Title Bar - Fixed at top */}
      {isDesktopApp && <TauriTitleBar />}
      {isDesktopApp && <TauriUpdateBannerSlot />}
      
      {/* Main content area */}
      <div
        className={cn(
          "flex h-full min-h-0 w-full overflow-hidden",
          isDesktopApp ? "bg-tauri-app-surface" : "bg-background dark:bg-[hsl(var(--cast-iron-skillet))]"
        )}
      >
        <AppSidebar>
          <div className="h-full">
            {auth.isAuthenticated && <UserPreferencesInitializer />}
            {auth.isAuthenticated && <ProfileImagePreloader />}
            {isDesktopApp && <BennyCompanionController />}
            {isDesktopApp && <RecorderController />}
            {isDesktopApp && <AssistantClientActionsController />}
            {isDesktopApp && <MeetingReminderController />}
            {auth.isAuthenticated ? (
              children
            ) : null}
            {auth.isAuthenticated && !isTauri() && !isMobile && (
              <OpenInDesktopPrompt />
            )}
          </div>
        </AppSidebar>
        {/* Live recording pane (Apple Music lyrics-style) — renders only
            while a recording is active and the panel is open */}
        {auth.isAuthenticated && !isPopup && !isMobile && (
          <LazyLiveRecordingPanel />
        )}
        {/* Benny is a flex sibling of the application surface, just like the
            live recording panel. Opening it narrows the active page instead
            of covering it, so navigation remains available. Mobile keeps its
            full-screen sheet behavior. */}
        {auth.isAuthenticated && !isPopup && <BennyChat />}
      </div>
    </SidebarProvider>
  )
} 
